import { isValidSerialNumberFormat } from "./serialNumberValidation";

/** Serial numbers may be separated by a newline, comma, semicolon or tab. */
const SERIAL_SEPARATORS = /[\r\n,;\t]+/;

export const parseBulkSerials = (text: string): string[] =>
  text
    .split(SERIAL_SEPARATORS)
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);

export type BulkPasteModel = {
  sku: string;
  desc: string;
  etype: string;
  /** Prefix used to match a pasted serial to this model. Models without one can only be forced. */
  serialPrefix?: string;
  /** Destination vendor code on the selected path. */
  vendor: string;
};

export type BulkPasteGroup<TCondition extends string> = {
  sku: string;
  serials: string[];
  condition: TCondition;
};

export type BulkPasteResult<TCondition extends string> = {
  groups: Array<BulkPasteGroup<TCondition>>;
  added: number;
  duplicates: number;
  /** Serials that matched no eligible model (or matched a model for another vendor). */
  unmatched: string[];
  /** Serials that are not 6 to 20 letters, digits or dashes. */
  invalid: string[];
};

type MatchOptions<TCondition extends string> = {
  serials: string[];
  models: BulkPasteModel[];
  /** Serials already on the request, so they are skipped as duplicates. */
  existingSerials: string[];
  /** When set, every serial is forced onto this model instead of being matched by prefix. */
  forcedSku?: string;
  condition: TCondition;
  /** Vendor the request is already locked to; matches for any other vendor are not added. */
  lockedVendor?: string;
  /** On a path that is not split by vendor, the first matched serial locks the vendor for the rest. */
  lockToFirstMatch: boolean;
};

/**
 * Matches each pasted serial to a model by prefix (longest prefix wins) and reports what was added,
 * what was skipped as a duplicate and what could not be matched.
 */
export const matchBulkSerials = <TCondition extends string>({
  serials,
  models,
  existingSerials,
  forcedSku,
  condition,
  lockedVendor,
  lockToFirstMatch,
}: MatchOptions<TCondition>): BulkPasteResult<TCondition> => {
  const seen = new Set(existingSerials.map((serial) => serial.trim().toUpperCase()).filter(Boolean));
  const prefixModels = models
    .filter((model) => model.serialPrefix?.trim())
    .map((model) => ({ model, prefix: model.serialPrefix!.trim().toUpperCase() }))
    .sort((a, b) => b.prefix.length - a.prefix.length);
  const forcedModel = forcedSku ? models.find((model) => model.sku === forcedSku) : undefined;

  let vendor = lockedVendor;
  const bySku = new Map<string, string[]>();
  const result: BulkPasteResult<TCondition> = { groups: [], added: 0, duplicates: 0, unmatched: [], invalid: [] };

  serials.forEach((serial) => {
    if (seen.has(serial)) {
      result.duplicates += 1;
      return;
    }
    if (!isValidSerialNumberFormat(serial)) {
      result.invalid.push(serial);
      return;
    }

    const model = forcedSku ? forcedModel : prefixModels.find((entry) => serial.startsWith(entry.prefix))?.model;
    if (!model || (vendor && model.vendor !== vendor)) {
      result.unmatched.push(serial);
      return;
    }
    if (!vendor && lockToFirstMatch) vendor = model.vendor;

    const list = bySku.get(model.sku) ?? [];
    list.push(serial);
    bySku.set(model.sku, list);
    seen.add(serial);
    result.added += 1;
  });

  bySku.forEach((list, sku) => result.groups.push({ sku, serials: list, condition }));
  return result;
};
